import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, MetricsHeader, ErrorState, Skeleton } from '@shared/design-system/ui'
import type { Currency } from '@shared/contracts/enums'
import { analyticsPeriod } from '@shared/lib/analytics-period'
import type { DateRangeValue } from '@shared/lib/date-range'
import {
  subscriptionMetricsQuery,
  SubscriptionMetricsSummary,
} from '@entities/subscription-metrics'
import { projectionPanel } from '@entities/subscription-metrics/ui/subscription-metrics.css'

type SubscriptionMetricsPanelProps = {
  dateRange?: DateRangeValue
  onDateRangeChange?: (value: DateRangeValue) => void
  currency?: Currency
  onCurrencyChange?: (currency: Currency) => void
}

export function SubscriptionMetricsPanel({
  onCurrencyChange,
  dateRange: controlledRange,
  onDateRangeChange,
}: SubscriptionMetricsPanelProps = {}) {
  const [localRange, setLocalRange] = useState<DateRangeValue>({
    preset: 'month',
    from: null,
    to: null,
  })
  const dateRange = controlledRange ?? localRange
  const period = analyticsPeriod(dateRange)
  const metrics = useQuery({
    ...subscriptionMetricsQuery(period.query, !period.error),
    placeholderData: keepPreviousData,
  })
  const selectedCurrency = 'BRL'
  const controls = (
    <MetricsHeader
      title="Assinaturas no período"
      titleId="subscription-metrics-title"
      dateRange={dateRange}
      onDateRangeChange={(value) => {
        setLocalRange(value)
        onDateRangeChange?.(value)
      }}
      periodLabel={`${period.label} · Busca e status filtram somente a lista.`}
      currency={selectedCurrency}
      currencies={metrics.data?.currencies ?? []}
      onCurrencyChange={(next) => onCurrencyChange?.(next)}
      currencyAriaLabel="Moeda das assinaturas"
    />
  )
  if (period.error)
    return (
      <Card
        padding="md"
        className={projectionPanel}
        role="region"
        aria-labelledby="subscription-metrics-title"
      >
        {controls}
        <p role="alert">{period.error}</p>
      </Card>
    )

  if (metrics.isPending || metrics.isPlaceholderData) {
    return (
      <Card
        padding="md"
        className={projectionPanel}
        role="region"
        aria-labelledby="subscription-metrics-title"
      >
        {controls}
        <Skeleton height={190} />
      </Card>
    )
  }
  if (metrics.isError) {
    return (
      <Card
        padding="md"
        className={projectionPanel}
        role="region"
        aria-labelledby="subscription-metrics-title"
      >
        {controls}
        <ErrorState onRetry={() => void metrics.refetch()} />
      </Card>
    )
  }

  return (
    <SubscriptionMetricsSummary
      metrics={metrics.data}
      currency={selectedCurrency}
      header={controls}
      titleId="subscription-metrics-title"
    />
  )
}
